import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { isSkuSearch, normalizeSku } from '~~/helpers/search/isSkuSearch';
import { addNativeRoutingToProductUrl } from '~~/helpers/routes/nativeRouting/transformProductsForNativeRouting';

export type SkuSearchResult = {
  name: string;
  url: string;
  image: string;
  imageAlt: string;
  price: number;
  sku: string;
} | null;

type ProductBySku = {
  slug: string;
  name: string;
  image: string;
  imageAlt: string;
  finalPrice: number;
  sku: string;
} | null;

export const useSkuSearch = () => {

  const { makeCommercetoolsRequest } = useExpondoMiddleware();
  const { countryCode } = useLocalization();
  const skuResult = ref<SkuSearchResult>(null);
  const isLoading = ref<boolean>(false);

  const search = async (value: string): Promise<void> => {

    if (!isSkuSearch(value)) {

      skuResult.value = null;

      return;

    }

    const sku = normalizeSku(value);

    isLoading.value = true;

    try {

      const response = await makeCommercetoolsRequest<ProductBySku>(
        {
          endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_PRODUCT_BY_SKU,
          data: { sku },
        },
      );

      const product = response?.data ?? null;

      if (!product?.name || !product?.slug) {

        skuResult.value = null;

        return;

      }

      skuResult.value = {
        name: product.name,
        url: addNativeRoutingToProductUrl(product.slug, countryCode.value),
        image: product.image,
        imageAlt: product.imageAlt,
        price: product.finalPrice,
        sku: product.sku,
      };

    } catch {

      skuResult.value = null;

    } finally {

      isLoading.value = false;

    }

  };

  const clear = () => {

    skuResult.value = null;

  };

  return {
    skuResult: computed(() => skuResult.value),
    isLoading: computed(() => isLoading.value),
    search,
    clear,
  };

};
